import React from "react";

const LoadingScreen = ({ message = "Crafting your beachside getaway..." }) => {
  return (
    <div className="modern-loader-container">
      <style>{`
        .modern-loader-container {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #07101e;
          background-image: 
            radial-gradient(circle at 50% 30%, rgba(30, 62, 98, 0.45) 0%, transparent 60%),
            radial-gradient(circle at 20% 80%, rgba(255, 71, 87, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(212, 175, 55, 0.15) 0%, transparent 50%);
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          overflow: hidden;
        }

        .loader-ambient-glow {
          position: absolute;
          width: 320px;
          height: 320px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 71, 87, 0.18) 0%, rgba(212, 175, 55, 0.1) 45%, transparent 70%);
          animation: ambientPulse 3.5s ease-in-out infinite alternate;
          pointer-events: none;
        }

        .loader-card-glass {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 36px 32px;
          max-width: 340px;
          width: 90%;
          background: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 28px;
          box-shadow: 
            0 20px 50px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
          text-align: center;
          animation: cardFloat 3s ease-in-out infinite alternate;
        }

        /* Central Rotating Rings & Monogram */
        .loader-orbit-wrapper {
          position: relative;
          width: 88px;
          height: 88px;
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .loader-ring-outer {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 2px solid transparent;
          border-top-color: #ff4757;
          border-right-color: rgba(255, 71, 87, 0.3);
          border-bottom-color: #d4af37;
          animation: spinOuter 1.4s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
          box-shadow: 0 0 18px rgba(255, 71, 87, 0.35);
        }

        .loader-ring-inner {
          position: absolute;
          inset: 8px;
          border-radius: 50%;
          border: 2px dashed rgba(255, 255, 255, 0.25);
          border-top-color: rgba(255, 255, 255, 0.9);
          animation: spinInner 2s linear infinite;
        }

        .loader-center-emblem {
          width: 52px;
          height: 52px;
          border-radius: 18px;
          background: linear-gradient(135deg, #0b192c 0%, #1e3e62 100%);
          border: 1.5px solid rgba(212, 175, 55, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
          animation: emblemPulse 2s ease-in-out infinite;
        }

        .loader-emblem-icon {
          font-size: 24px;
          line-height: 1;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
        }

        /* Typography */
        .loader-brand-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          background: linear-gradient(135deg, #ffffff 30%, #fed7aa 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 6px;
        }

        .loader-subtext {
          font-size: 0.84rem;
          font-weight: 400;
          color: #94a3b8;
          line-height: 1.4;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        /* Shimmer Progress Track */
        .loader-progress-track {
          width: 140px;
          height: 3px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 6px;
          margin-top: 18px;
          overflow: hidden;
          position: relative;
        }

        .loader-progress-bar {
          position: absolute;
          top: 0;
          left: -100%;
          width: 70%;
          height: 100%;
          background: linear-gradient(90deg, transparent, #ff4757, #d4af37, transparent);
          border-radius: 6px;
          animation: shimmerLine 1.6s ease-in-out infinite;
        }

        /* Dots Animation */
        .loader-dot {
          animation: dotBlink 1.4s infinite both;
          color: #ff4757;
          font-weight: 700;
        }
        .loader-dot:nth-child(2) { animation-delay: 0.2s; }
        .loader-dot:nth-child(3) { animation-delay: 0.4s; }

        @keyframes spinOuter {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes spinInner {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }

        @keyframes ambientPulse {
          0% { transform: scale(0.9); opacity: 0.6; }
          100% { transform: scale(1.15); opacity: 1; }
        }

        @keyframes cardFloat {
          0% { transform: translateY(0); }
          100% { transform: translateY(-5px); }
        }

        @keyframes emblemPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }

        @keyframes shimmerLine {
          0% { left: -70%; }
          100% { left: 100%; }
        }

        @keyframes dotBlink {
          0%, 80%, 100% { opacity: 0.2; }
          40% { opacity: 1; }
        }
      `}</style>

      <div className="loader-ambient-glow" />

      <div className="loader-card-glass">
        <div className="loader-orbit-wrapper">
          <div className="loader-ring-outer" />
          <div className="loader-ring-inner" />
          <div className="loader-center-emblem">
            <span className="loader-emblem-icon">🏖️</span>
          </div>
        </div>

        <h3 className="loader-brand-title">Ganesh Holiday Homes</h3>
        <p className="loader-subtext">
          <span>{message}</span>
          <span className="loader-dot">.</span>
          <span className="loader-dot">.</span>
          <span className="loader-dot">.</span>
        </p>

        <div className="loader-progress-track">
          <div className="loader-progress-bar" />
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
